'use client';

import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/app/lib/utils';
import Input from '@/app/components/ui/Input';
import CustomSelect, { Option } from '@/app/components/ui/CustomSelect';
import { Button } from '@/app/components/ui/Button';
import Tooltip from '@/app/components/ui/Tooltip';
import Pill, { PillColor } from '@/app/components/ui/Pill';
import TimePicker from '@/app/components/ui/TimePicker';
import type {
  WebsiteUrlRule,
  WebsiteUrlRuleOperator,
  TargetPageMatchType,
  TargetPageStatus,
} from '@/types/websiteUrlRule';
import { getTargetPages } from '@/api/target-pages';
import { TargetPage } from '@/types/targetPage';

type LegacyWebsiteUrlRuleOperator =
  | WebsiteUrlRuleOperator
  | 'greater_than_equals'
  | 'less_than_equals'
  | 'greater_equals';

type WebsiteUrlRulesInputLabels = {
  title?: string;
  description?: string;
  enterUrl: string;
  visitsPlaceholder: string;
  visitCount: string;
  targetPages: string;
  totalTimeSpent: string;
  timeSpentPlaceholder: string;
  exactMatch: string;
  matchTypes?: Record<TargetPageMatchType, string>;
  visits: string;
  time: string;
  clearTime: string;
  minuteSuffix: string;
  secondSuffix: string;
  add: string;
  remove: string;
  operators: Record<WebsiteUrlRuleOperator, string>;
};

type WebsiteUrlRulesInputProps = {
  value: WebsiteUrlRule[];
  onChange: (rules: WebsiteUrlRule[]) => void;
  onDraftChange?: (rule: Partial<WebsiteUrlRule>) => void;
  labels: WebsiteUrlRulesInputLabels;
  maxRules?: number;
  layout?: 'compact' | 'inline';
  showHeader?: boolean;
  includeTargetPageMetadata?: boolean;
  isActive?: boolean;
  wrapControls?: boolean;
  className?: string;
};

const operatorDisplaySymbols: Record<WebsiteUrlRuleOperator, string> = {
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '≥',
  less_than_or_equal: '≤',
};

type OperatorState = WebsiteUrlRuleOperator | '';

const emptyOperator: OperatorState = '';
const fallbackDisplayOperator: WebsiteUrlRuleOperator = 'greater_than';

const normalizeOperator = (value: LegacyWebsiteUrlRuleOperator): WebsiteUrlRuleOperator => {
  if (value === 'greater_than_equals' || value === 'greater_equals') {
    return 'greater_than_or_equal';
  }
  if (value === 'less_than_equals') {
    return 'less_than_or_equal';
  }
  return value;
};

const normalizeUrl = (value: string) => {
  if (!value.trim()) return '';

  try {
    const withProtocol =
      value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`;
    const parsed = new URL(withProtocol);
    return `${parsed.hostname.replace(/^www\./, '').toLowerCase()}${parsed.pathname.replace(
      /\/$/,
      ''
    )}${parsed.search}`.toLowerCase();
  } catch {
    return value
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .toLowerCase();
  }
};

const formatDuration = (
  seconds: number,
  labels: Pick<WebsiteUrlRulesInputLabels, 'minuteSuffix' | 'secondSuffix'>
) => {
  const totalSeconds = Math.floor(seconds);
  if (totalSeconds < 60) return `${totalSeconds}${labels.secondSuffix}`;

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return remainingSeconds > 0
    ? `${minutes}${labels.minuteSuffix} ${remainingSeconds}${labels.secondSuffix}`
    : `${minutes}${labels.minuteSuffix}`;
};

export default function WebsiteUrlRulesInput({
  value,
  onChange,
  onDraftChange,
  labels,
  maxRules = 5,
  layout = 'compact',
  showHeader = false,
  includeTargetPageMetadata = false,
  isActive = true,
  wrapControls = false,
  className,
}: WebsiteUrlRulesInputProps) {
  const { clientId } = useParams<{ clientId: string }>();
  const [url, setUrl] = useState('');
  const [operator, setOperator] = useState<OperatorState>(emptyOperator);
  const [count, setCount] = useState('');
  const [timeSpentOperator, setTimeSpentOperator] = useState<OperatorState>(emptyOperator);
  const [timeSpent, setTimeSpent] = useState('');
  const [selectedTargetPage, setSelectedTargetPage] = useState<TargetPage | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { data: targetPagesData } = useQuery({
    queryKey: ['website-url-rules-target-pages', clientId],
    queryFn: () => getTargetPages({ clientId: clientId || '', limit: 100 }),
    enabled: !!clientId && isActive,
    refetchOnMount: true,
    staleTime: 0,
  });

  const operatorOptions: Option[] = useMemo(
    () =>
      (Object.keys(labels.operators) as WebsiteUrlRuleOperator[]).map(key => ({
        id: key,
        value: labels.operators[key],
      })),
    [labels.operators]
  );

  const normalizedUrl = url.trim();
  const parsedCount = Number(count);
  const parsedTimeSpent = Number(timeSpent);
  const targetPageOptions = useMemo(() => targetPagesData?.data || [], [targetPagesData?.data]);
  const filteredTargetPages = useMemo(() => {
    const search = normalizedUrl.toLowerCase();
    if (!search) return targetPageOptions.slice(0, 8);

    return targetPageOptions
      .filter(targetPage =>
        [targetPage.url, targetPage.name, targetPage.short_name].some(value =>
          value?.toLowerCase().includes(search)
        )
      )
      .slice(0, 8);
  }, [targetPageOptions, normalizedUrl]);
  const canAdd =
    normalizedUrl.length > 0 &&
    !!operator &&
    Number.isInteger(parsedCount) &&
    parsedCount > 0 &&
    (timeSpent.trim().length === 0 ||
      (!!timeSpentOperator && Number.isFinite(parsedTimeSpent) && parsedTimeSpent > 0)) &&
    value.length < maxRules;

  useEffect(() => {
    if (!onDraftChange) return;

    onDraftChange({
      ...(normalizedUrl ? { url: normalizedUrl } : {}),
      ...(operator ? { operator } : {}),
      ...(count.trim() && Number.isFinite(parsedCount) ? { count: parsedCount } : {}),
      ...(includeTargetPageMetadata && selectedTargetPage
        ? {
            target_page_id: selectedTargetPage.id,
            target_page_short_name: selectedTargetPage.short_name,
          }
        : {}),
      ...(timeSpentOperator ? { time_spent_operator: timeSpentOperator } : {}),
      ...(timeSpent.trim() && Number.isFinite(parsedTimeSpent)
        ? { time_spent_seconds: parsedTimeSpent }
        : {}),
    });
  }, [
    count,
    includeTargetPageMetadata,
    normalizedUrl,
    onDraftChange,
    operator,
    parsedCount,
    parsedTimeSpent,
    selectedTargetPage,
    timeSpent,
    timeSpentOperator,
  ]);

  const handleAdd = () => {
    if (!canAdd) return;
    const matchedTargetPage =
      selectedTargetPage ||
      targetPageOptions.find(targetPage => normalizeUrl(targetPage.url) === normalizeUrl(url));
    const nextRule: WebsiteUrlRule = {
      url: normalizedUrl,
      operator: operator as WebsiteUrlRuleOperator,
      count: parsedCount,
      ...(includeTargetPageMetadata && matchedTargetPage
        ? {
            target_page_id: matchedTargetPage.id,
            target_page_short_name: matchedTargetPage.short_name,
          }
        : {}),
      ...(timeSpent.trim().length > 0
        ? {
            time_spent_operator: timeSpentOperator as WebsiteUrlRuleOperator,
            time_spent_seconds: parsedTimeSpent,
          }
        : {}),
    };

    onChange([...value, nextRule]);
    setUrl('');
    setOperator(emptyOperator);
    setCount('');
    setTimeSpentOperator(emptyOperator);
    setTimeSpent('');
    setSelectedTargetPage(null);
    setShowSuggestions(false);
  };

  const handleRemove = (indexToRemove: number) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const getRuleTargetPage = (rule: WebsiteUrlRule) => {
    const ruleUrl = rule.url || '';

    return targetPageOptions.find(
      targetPage =>
        targetPage.id === rule.target_page_id ||
        (!!ruleUrl && normalizeUrl(targetPage.url) === normalizeUrl(ruleUrl)) ||
        (!!rule.target_page_short_name && targetPage.short_name === rule.target_page_short_name)
    );
  };

  const getRuleDisplayUrl = (rule: WebsiteUrlRule) => {
    return rule.url || getRuleTargetPage(rule)?.url || '';
  };

  const getRuleTargetPageShortName = (rule: WebsiteUrlRule) => {
    return rule.target_page_short_name || getRuleTargetPage(rule)?.short_name || '';
  };

  const getRuleTargetPageMatchType = (rule: WebsiteUrlRule) => {
    return rule.target_page_match_type;
  };

  const getRuleTargetPageStatus = (rule: WebsiteUrlRule) => {
    return rule.target_page_status;
  };

  const formatMatchType = (matchType?: TargetPageMatchType) => {
    if (!matchType) return '';

    return (
      labels.matchTypes?.[matchType] || (matchType === 'exact' ? labels.exactMatch : matchType)
    );
  };

  const formatStatus = (status: TargetPageStatus) =>
    (
      ({
        active: 'Active',
        inactive: 'Inactive',
        disabled: 'Disabled',
      }) satisfies Record<TargetPageStatus, string>
    )[status] || status;

  const getStatusColor = (status: TargetPageStatus): PillColor =>
    (
      ({
        active: 'success',
        inactive: 'neutral',
        disabled: 'destructive',
      }) satisfies Record<TargetPageStatus, PillColor>
    )[status] || 'neutral';

  const visitControls = (
    <>
      <CustomSelect
        options={operatorOptions}
        defaultValue={operator}
        onOptionSelect={selected => {
          const nextOperator = (selected as Option | undefined)?.id;
          if (nextOperator) {
            setOperator(nextOperator.toString() as WebsiteUrlRuleOperator);
          }
        }}
        wrapperClassNames="min-w-0"
        triggerClassNames="h-[38px] rounded-lg shadow-xs"
        contentClassNames="min-w-80"
        placeholder="Select"
      />
      <div className="min-w-0">
        <Input
          type="number"
          value={count}
          onChange={event => setCount(event.target.value)}
          placeholder={labels.visitsPlaceholder}
          className="h-[38px] rounded-lg shadow-xs"
        />
      </div>
    </>
  );

  const timeSpentControls = (
    <>
      <CustomSelect
        options={operatorOptions}
        defaultValue={timeSpentOperator}
        onOptionSelect={selected => {
          const nextOperator = (selected as Option | undefined)?.id;
          if (nextOperator) {
            setTimeSpentOperator(nextOperator.toString() as WebsiteUrlRuleOperator);
          }
        }}
        wrapperClassNames="min-w-0"
        triggerClassNames="h-[38px] rounded-lg shadow-xs"
        contentClassNames="min-w-80"
        placeholder="Select"
      />
      <TimePicker
        mode="duration"
        secondsValue={timeSpent}
        onSecondsChange={setTimeSpent}
        placeholder={labels.timeSpentPlaceholder}
        durationLabels={{
          clearTime: labels.clearTime,
          minuteSuffix: labels.minuteSuffix,
          secondSuffix: labels.secondSuffix,
        }}
        className="min-w-0"
        triggerClassName="h-[38px] rounded-lg shadow-xs px-2"
      />
    </>
  );

  const targetPageInput = (
    <div className="relative">
      <Input
        value={url}
        onChange={event => {
          setUrl(event.target.value);
          setSelectedTargetPage(null);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => {
          window.setTimeout(() => setShowSuggestions(false), 150);
        }}
        placeholder={labels.enterUrl}
        className="h-[38px] rounded-lg shadow-xs"
      />
      {showSuggestions && filteredTargetPages.length > 0 && (
        <div className="absolute left-0 right-0 top-[42px] z-[60] max-h-64 overflow-y-auto rounded-lg border border-border bg-background p-1 shadow-md">
          {filteredTargetPages.map(targetPage => (
            <button
              key={targetPage.id}
              type="button"
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-accent"
              onMouseDown={event => {
                event.preventDefault();
                setUrl(targetPage.url);
                setSelectedTargetPage(targetPage);
                setShowSuggestions(false);
              }}
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">{targetPage.url}</div>
                {targetPage.name && (
                  <div className="truncate text-xs text-muted-foreground">{targetPage.name}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {targetPage.short_name && (
                  <Pill variant="outline" color="purple" rounded={false} className="shrink-0">
                    {targetPage.short_name}
                  </Pill>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={cn('space-y-4', className)}>
      {showHeader && (
        <div className="space-y-1">
          {labels.title && <h3 className="text-sm font-medium text-foreground">{labels.title}</h3>}
          {labels.description && (
            <p className="text-sm text-muted-foreground">{labels.description}</p>
          )}
        </div>
      )}

      {layout === 'inline' ? (
        <div className="grid grid-cols-[6fr_5fr_3fr_auto] items-center gap-3">
          <div className="min-w-0">
            <Input
              value={url}
              onChange={event => setUrl(event.target.value)}
              placeholder={labels.enterUrl}
              className="h-[38px] rounded-lg shadow-xs"
            />
          </div>
          {visitControls}
          <Button
            type="button"
            variant="outline"
            color="blue"
            size="sm"
            onClick={handleAdd}
            disabled={!canAdd}
            className="h-[38px]"
          >
            {labels.add}
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-muted p-4">
          <div className="flex flex-1 flex-col gap-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              {labels.targetPages}
            </label>
            {targetPageInput}
          </div>
          <div
            className={cn(
              'mt-4 gap-4',
              wrapControls ? 'flex flex-col' : 'flex flex-col md:flex-row'
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                {labels.visitCount}
              </label>
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(112px,1fr)] items-center gap-3">
                {visitControls}
              </div>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                {labels.totalTimeSpent}
              </label>
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(112px,1fr)] items-center gap-3">
                {timeSpentControls}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            color="blue"
            size="sm"
            onClick={handleAdd}
            disabled={!canAdd}
            className="mt-4 h-[32px] gap-2 rounded-lg"
          >
            <Plus className="size-4" />
            {labels.add}
          </Button>
        </div>
      )}

      {value.length > 0 && (
        <div className="space-y-3 border-t border-border pt-4">
          {value.map((rule, index) => {
            const ruleDisplayUrl = getRuleDisplayUrl(rule);
            const ruleTargetPageShortName = getRuleTargetPageShortName(rule);
            const ruleTargetPageMatchType = formatMatchType(getRuleTargetPageMatchType(rule));
            const ruleTargetPageStatus = getRuleTargetPageStatus(rule);
            const isInactiveTargetPageRule =
              !!ruleTargetPageStatus && ruleTargetPageStatus !== 'active';

            return (
              <div
                key={`${rule.target_page_id || ruleDisplayUrl}-${rule.operator}-${rule.count}-${index}`}
                className="relative flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2 md:flex-nowrap"
              >
                <div className="flex min-w-0 flex-1 items-center">
                  <Tooltip
                    label={ruleDisplayUrl}
                    side="top"
                    align="center"
                    delayDuration={0}
                    className="rounded bg-foreground px-3 py-1.5 text-xs text-background"
                    arrowClassName="fill-foreground"
                  >
                    <div
                      className={cn(
                        'flex min-w-0 items-center gap-2',
                        ruleTargetPageShortName &&
                          'border border-border border-dashed p-1 rounded-md'
                      )}
                    >
                      {ruleTargetPageShortName && (
                        <Pill variant="outline" color="purple" rounded={false}>
                          {ruleTargetPageShortName}
                        </Pill>
                      )}
                      {ruleTargetPageMatchType && (
                        <Pill color="info" variant="soft" rounded={false}>
                          {ruleTargetPageMatchType}
                        </Pill>
                      )}
                      {!ruleTargetPageShortName && (
                        <span className="inline-block max-w-full truncate font-medium text-foreground">
                          {ruleDisplayUrl}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {isInactiveTargetPageRule ? (
                    <Pill variant="soft" color={getStatusColor(ruleTargetPageStatus)}>
                      {formatStatus(ruleTargetPageStatus)}
                    </Pill>
                  ) : (
                    <>
                      <Pill color="info" variant="soft">
                        {labels.visits}{' '}
                        {
                          operatorDisplaySymbols[
                            normalizeOperator(rule.operator as LegacyWebsiteUrlRuleOperator)
                          ]
                        }{' '}
                        {rule.count}
                      </Pill>
                      {rule.time_spent_seconds ? (
                        <Pill color="info" variant="soft">
                          {labels.time}{' '}
                          {
                            operatorDisplaySymbols[
                              normalizeOperator(
                                (rule.time_spent_operator ||
                                  fallbackDisplayOperator) as LegacyWebsiteUrlRuleOperator
                              )
                            ]
                          }{' '}
                          {formatDuration(rule.time_spent_seconds, labels)}
                        </Pill>
                      ) : null}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="absolute -right-2 -top-2 flex size-4 items-center justify-center rounded-full bg-border text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemove(index)}
                  aria-label={labels.remove}
                >
                  <X className="size-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
